/**
 * useInvestigationCacheStore — Per-node Auto-Investigate cache.
 *
 * Mirrors useFtmStore: each canvas node that has completed Targeted Node
 * Intelligence keeps its section payloads here keyed by `${chain}:${address}`.
 * Switching nodes no longer wipes the prior run — returning to that node
 * re-hydrates the card from cache.
 *
 * Serialized into `graph_state.investigation_cache` on investigation save.
 */
import { create } from 'zustand';
import type { CirculationResult, InvestigationKey } from '../hooks/useAutoInvestigation';

export interface InvestigationCacheEntry {
    chain: string;
    address: string;
    /** exportForSave() payload — section data keyed by InvestigationKey */
    sections: Partial<Record<InvestigationKey, unknown>>;
    circulation: CirculationResult | null;
    ranAt: string;
}

interface InvestigationCacheState {
    entries: Record<string, InvestigationCacheEntry>;
}

interface InvestigationCacheActions {
    getEntry: (
        chain: string | null | undefined,
        address: string | null | undefined,
    ) => InvestigationCacheEntry | null;
    setEntry: (
        chain: string,
        address: string,
        sections: Partial<Record<InvestigationKey, unknown>>,
        circulation?: CirculationResult | null,
    ) => void;
    clearEntry: (chain: string, address: string) => void;
    clearAll: () => void;
    hydrate: (entries: Record<string, InvestigationCacheEntry>) => void;
    exportForSave: () => Record<string, InvestigationCacheEntry>;
}

const keyFor = (chain: string, address: string) =>
    `${(chain || '').toUpperCase()}:${address}`;

export const useInvestigationCacheStore = create<InvestigationCacheState & InvestigationCacheActions>((set, get) => ({
    entries: {},

    getEntry: (chain, address) => {
        if (!chain || !address) return null;
        return get().entries[keyFor(chain, address)] || null;
    },

    setEntry: (chain, address, sections, circulation = null) => {
        const key = keyFor(chain, address);
        set(state => ({
            entries: {
                ...state.entries,
                [key]: {
                    chain: (chain || '').toUpperCase(),
                    address,
                    sections,
                    circulation,
                    ranAt: new Date().toISOString(),
                },
            },
        }));
    },

    clearEntry: (chain, address) => {
        const key = keyFor(chain, address);
        set(state => {
            const next = { ...state.entries };
            delete next[key];
            return { entries: next };
        });
    },

    clearAll: () => set({ entries: {} }),

    hydrate: (entries) => set({ entries: entries || {} }),

    exportForSave: () => get().entries,
}));
